<template>
  <div ref="videoPlayerYTRef" class="widget-player-container aspect-video w-full">
    <video
      :id="videoContentEl"
      ref="videoElement"
      muted
      playsinline
      class="entry-video-item video-js w-full h-full vjs-fluid vjs-16-9 relative"
    />
  </div>
</template>

<script>
import { defineComponent, useMeta, ref, onMounted, watch, computed, onUnmounted, useRoute, onBeforeUnmount } from '@nuxtjs/composition-api';
import { useIntersectionObserver } from '@vueuse/core';
import { uniqueId } from 'lodash';
import { useDevice } from '~/composables/useDevice';
import { HOST_URL, LANG } from '~/constants/config';
import { eventBus } from '~/modules/common/utils/eventBus';
import { generateVideoSchema } from '~/modules/common/utils/schemaUtils';
import { DESKTOP_AD_UNIT, MOBILE_AD_UNIT } from '~/modules/video/constants/adUnit';
export default defineComponent({
  head: {},
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { isMobile } = useDevice();
    const route = useRoute();
    const article = ref(props.article);
    const videoData = computed(() => props.article.videoEntry.videoData);
    const videoContentEl = computed(() => 'video_' + videoData.value.id);
    const videoElement = ref(null);
    const isYoutube = computed(() => videoData.value.source === 'youtube');
    const type = computed(() => (isYoutube.value ? 'video/youtube' : 'application/x-mpegURL'));
    const techOrder = computed(() => ['Youtube', 'Html5']);
    const url = computed(() => {
      if (isYoutube.value) {
        return `www.youtube.com/watch?v=${videoData.value.id}?rel=0`;
      } else {
        return videoData.value.url;
      }
    });
    let player;
    let videojs;
    let playerOptions;

    // Meta ---------------------------
    const innerHTML = JSON.stringify(generateVideoSchema(article.value.videoEntry, article.value), null, 2);
    const hid = uniqueId('nuxt-jsonld-');
    useMeta(() => ({
      script: [
        {
          hid,
          src: 'https://imasdk.googleapis.com/js/sdkloader/ima3.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'https://cdnjs.cloudflare.com/ajax/libs/video.js/8.5.2/video.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'https://cdnjs.cloudflare.com/ajax/libs/video.js/8.5.2/lang/tr.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'https://cdnjs.cloudflare.com/ajax/libs/videojs-contrib-ads/7.3.2/videojs-contrib-ads.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'https://cdnjs.cloudflare.com/ajax/libs/videojs-contrib-quality-levels/4.0.0/videojs-contrib-quality-levels.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'https://cdnjs.cloudflare.com/ajax/libs/videojs-ima/2.2.0/videojs.ima.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          type: 'application/ld+json',
          innerHTML,
        },
      ],
      link: [
        {
          href: 'https://cdnjs.cloudflare.com/ajax/libs/video.js/8.5.2/video-js.min.css',
          rel: 'stylesheet',
          integrity: 'sha512-+CRWio9t8I0yMYqwHOQWIybOeIlfACzx/HkiJAvhhpPD0G310qhPkhh0xSnPhAH0hXijZoxUoTEEL5RzSyvOow==',
          crossorigin: 'anonymous',
          referrerpolicy: 'no-referrer',
        },
        {
          href: 'https://cdnjs.cloudflare.com/ajax/libs/videojs-ima/2.2.0/videojs.ima.min.css',
          rel: 'stylesheet',
          integrity: 'sha512-hw6cZAVUjqC0sNVfGtFdnmgTsNLElCJIL4Ek0QPfEQUSMTuOhVkBjEW4nrN++PFeZVh3j1b2oenyoG3b8VQaag==',
          crossorigin: 'anonymous',
          referrerpolicy: 'no-referrer',
        },
        {
          href: '/css/custom.video.css',
          rel: 'stylesheet',
          preload: true,
        },
      ],
      __dangerouslyDisableSanitizersByTagID: {
        [hid]: ['innerHTML'],
      },
    }));

    const adParameters = computed(() => {
      const categoriesId = props.article.categories.map((category) => category._id);
      if (categoriesId.length) {
        // INFO: &etiket=prerolltest (local üzerinde reklamları oynatmak için)
        return encodeURIComponent(`kategori=${categoriesId.toString()}`);
      }
    });

    const adTagUrl = computed(() => {
      return `https://pubads.g.doubleclick.net/gampad/ads?iu=/21814681/${
        isMobile.value ? MOBILE_AD_UNIT : DESKTOP_AD_UNIT
      }&description_url=${encodeURI(
        HOST_URL + route.value.path
      )}&tfcd=0&npa=0&sz=640x480&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&plcmt=1&vpa=auto&vpmute=1&wta=1&vpos=preroll&impl=s&correlator=${Date.now()}&cust_params=${
        adParameters.value
      }`;
    });

    onMounted(() => {
      if (!window.videojs) return;
      videojs = require('video.js');
      require('videojs-youtube'); // youtube player plugin

      playerOptions = {
        muted: true,
        debug: false,
        autoplay: true,
        controls: true,
        preload: 'auto',
        errorDisplay: false,
        controlBar: {
          volumePanel: {
            inline: true,
          },
          pictureInPictureToggle: false,
        },
        language: LANG,
        html5: {
          nativeVideoTracks: true,
          nativeAudioTracks: true,
          hls: {
            overrideNative: !videojs.browser.IS_ANY_SAFARI, // eslint-disable-line no-undef
            enableWorker: true,
          },
          vhs: {
            activeLowInitialPlaylist: true,
            limitRenditionByPlayerDimensions: true,
            overrideNative: !videojs.browser.IS_ANY_SAFARI, // eslint-disable-line no-undef
            enableWorker: true,
          },
        },
        techOrder: techOrder.value,
      };

      // eslint-disable-next-line no-undef
      player = videojs(videoElement.value, {
        ...playerOptions,
        sources: [
          {
            type: type.value,
            src: url.value,
          },
        ],
      });

      let startEvent = 'click';
      if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/Android/i)) {
        startEvent = 'touchend';
      }

      const initAdDisplayContainer = function () {
        player.ima.initializeAdDisplayContainer();
        wrapperDiv.removeEventListener(startEvent, initAdDisplayContainer);
      };

      const wrapperDiv = document.getElementById(videoContentEl.value);
      if (player && player.ima) {
        try {
          player.ima({ id: `video_${videoData.id}`, adTagUrl: adTagUrl.value, locale: LANG });
          wrapperDiv.addEventListener(startEvent, initAdDisplayContainer);
        } catch (error) {}
      }

      // calculate player height for 16/9 ratio
      if (isMobile.value) {
        const playerWidth = window.innerWidth - 32;
        const playerHeight = (playerWidth * 9) / 16;
        wrapperDiv.style.height = playerHeight + 'px';
      } else {
        const playerWidth = wrapperDiv.style.width;
        const playerHeight = (playerWidth * 9) / 16;
        wrapperDiv.style.height = playerHeight + 'px';
      }

      player.on('play', () => {
        eventBus.emit('widget-player-status', true);
      });
      player.on('pause', () => {
        eventBus.emit('widget-player-status', false);
      });
      player.on('ended', () => {
        eventBus.emit('widget-player-video-ended');
      });

      handleVolumeBarListener();
    });

    // fix: library volume hover action not working
    const handleVolumeBarListener = () => {
      const volumeButton = document.getElementsByClassName('vjs-volume-panel')[0];
      if (!volumeButton) return;
      volumeButton.addEventListener('mouseover', () => {
        volumeButton.classList.add('vjs-hover');
      });

      volumeButton.addEventListener('mouseout', () => {
        volumeButton.classList.remove('vjs-hover');
      });
    };

    const changePlayingStatus = (play) => {
      if (!player) return;
      if (play) {
        player.play();
      } else {
        player.pause();
      }
    };

    eventBus.on('widget-player-playing-trigger', changePlayingStatus);

    onBeforeUnmount(() => {
      eventBus.off('widget-player-playing-trigger', changePlayingStatus);
      player.off('play');
      player.off('pause');
      player.off('ended');
    });

    watch(
      () => props.article,
      () => {
        if (player && player.ima) {
          try {
            player.ima.changeAdTag(adTagUrl.value);
            player.ima.requestAds();
          } catch (error) {}
        }
        player.src({ type: type.value, src: url.value });
        setTimeout(() => {
          player.ready(() => {
            player.play();
          });
        }, 100);
      },
      { deep: true }
    );

    const videoPlayerYTRef = ref(null);
    const { stop } = useIntersectionObserver(
      videoPlayerYTRef,
      ([{ isIntersecting }]) => {
        if (isIntersecting) {
          player.play();
        } else {
          player.pause();
        }
      },
      {
        root: null,
        rootMargin: '-10% 0% -35% 0%',
        threshold: 0.7,
      }
    );
    onUnmounted(() => {
      stop();
    });

    return { videoContentEl, videoElement, videoPlayerYTRef };
  },
});
</script>
<style lang="scss">
.ima-ad-container {
  z-index: 2 !important;
}
</style>
